import { Injectable } from "@angular/core";
import { ComponentStore } from "@ngrx/component-store";
import { catchError, map, Observable, switchMap, tap } from "rxjs";
import { PhotoModel } from "src/app/models/photo-model";
import { UploadPhotoModel } from "src/app/models/upload-photo-model";
import { CompanyLogoService } from "src/app/services/company-logo.service";
import { CompanyLogoState } from "./company-logo.state";

const initialState: CompanyLogoState = {
    loading: false,
    loaded: false,
    success: false,
    errorMessage: null,
    logo: null,
    uploadPhoto: null
}

@Injectable({
    providedIn: 'root'
})
export class CompanyLogoComponentStore extends ComponentStore<CompanyLogoState> {
    constructor(private readonly companyLogoService: CompanyLogoService) {
        super(initialState);
    }

    readonly logo$: Observable<PhotoModel> = this.select(state => state.logo);
    readonly uploadPhoto$: Observable<UploadPhotoModel> = this.select(state => state.uploadPhoto);
    readonly loading$: Observable<boolean> = this.select(state => state.loading);
    readonly loaded$: Observable<boolean> = this.select(state => state.loaded);
    readonly success$: Observable<boolean> = this.select(state => state.success);
    readonly errorMessage$: Observable<any> = this.select(state => state.errorMessage);

    readonly viewModel$ = this.select(
        this.logo$,
        this.uploadPhoto$,
        this.loaded$,
        this.loading$,
        this.success$,
        this.errorMessage$,
        (logo, uploadPhoto, loaded, loading, success, errorMessage) => ({
            logo, uploadPhoto, loaded, loading, success, errorMessage
        })
    );

    readonly setInitial = this.updater((_: CompanyLogoState) => {
        return {
            ...initialState
        };
    });

    readonly setLoading = this.updater((state: CompanyLogoState) => {
        return {
            ...state,
            loading: true,
            loaded: false,
        };
    });

    readonly setLoaded = this.updater((state: CompanyLogoState) => {
        return {
            ...state,
            loading: false,
            loaded: true,
        };
    });

    readonly updateCompanyLogoState = this.updater((state: CompanyLogoState, value: {
        logo: PhotoModel
    }) => {
        return {
            ...state,
            loading: false,
            loaded: true,
            success: true,
            logo: value.logo,
            uploadPhoto : null
        };
    });

    readonly setError = this.updater((state: CompanyLogoState, value: {
        errorMessage: string
    }) => {
        return {
            ...state,
            loading: false,
            loaded: true,
            success: false,
            errorMessage: value.errorMessage,
        };
    });

    readonly getCompanyLogo = this.effect((params$: Observable<{ companyId: string }>) => {
        this.setLoading();
        return params$.pipe(
            switchMap((params => this.companyLogoService.getCompanyLogo(params.companyId).pipe(
                tap({
                    next: (companyLogo) => {
                        this.updateCompanyLogoState({ logo: companyLogo });
                        this.setLoaded();
                    },
                    error: (e) => {
                        throw e;
                    }
                }),
                catchError(async (error) => {
                    this.setError({ errorMessage: error })
                    this.setLoaded();
                    this.setInitial();
                })
            ))
            )
        );
    });

    readonly uploadCompanyLogo = this.effect((params$: Observable<{ companyId: string, uploadPhoto: UploadPhotoModel }>) => {
        this.setLoading();
        return params$.pipe(
            switchMap((params => this.companyLogoService.uploadCompanyLogo(params.companyId, params.uploadPhoto).pipe(
                tap({
                    next: (companyLogo) => {
                        this.updateCompanyLogoState({ logo: companyLogo });
                        this.setLoaded();
                    },
                    error: (e) => {
                        throw e;
                    }
                }),
                catchError(async (error) => {
                    this.setError({ errorMessage: error })
                    this.setLoaded();
                    this.setInitial();
                })
            ))
            )
        );
    });

    readonly deleteCompanyLogo = this.effect((params$: Observable<{ companyId: string }>) => {
        this.setLoading();
        return params$.pipe(
            switchMap((params => this.companyLogoService.deleteCompanyLogo(params.companyId).pipe(
                map(() => {
                    this.updateCompanyLogoState({ logo: null });
                    this.setLoaded();
                }),
                catchError(async (error) => {
                    this.setError({ errorMessage: error })
                    this.setLoaded();
                    this.setInitial();
                })
            ))
            )
        );
    });
}